import React from 'react'
import { FaCheck, FaTimes, FaMinus, FaExclamationTriangle } from 'react-icons/fa'
import { Button, Heading, Stack, Text, Box, Icon, Tag, SimpleModal } from '@/components/lib'
import scorchHelpful from '../../../images/scorch/helpful.png'

const DOCUMENT_KINDS = {
  protocol: 'Protocol',
  participantInformationSheet: 'Participant information sheet',
  informedConsentForm: 'Informed consent form',
  questionnaire: 'Questionnaire',
  other: 'Other document'
}

// One line of the report: an outcome icon, what it concerns, and any notes
// Scorch left against it.
function Row ({ status, children, notes }) {
  const icon = {
    done: { as: FaCheck, color: 'green.500' },
    todo: { as: FaExclamationTriangle, color: 'orange.500' },
    failed: { as: FaTimes, color: 'red.500' },
    skipped: { as: FaMinus, color: 'fg.muted' }
  }[status]
  return (
    <Stack direction='row' gap={3} alignItems='flex-start'>
      <Icon as={icon.as} color={icon.color} mt={1} flexShrink={0} />
      <Box>
        <Text>{children}</Text>
        {notes?.map((note, i) => <Text key={i} fontSize='sm' color='fg.muted'>{note}</Text>)}
      </Box>
    </Stack>
  )
}

function Section ({ title, children }) {
  return (
    <Stack gap={2}>
      <Heading as='h4' size='sm'>{title}</Heading>
      {children}
    </Stack>
  )
}

// What Scorch built and what it left for the team, shown when a build
// finishes and before the team is taken to the trial. `report` and
// `warnings` are the API's, as returned by the build job.
export default function BuildReport ({ isOpen, report, warnings, onClose }) {
  if (!report) return null
  const { documents, trial, consent, stages } = report
  const consentRow = {
    enabled: {
      status: 'done',
      text: 'eConsent switched on and a consent form created',
      notes: [
        `${consent.conditions || 0} condition${consent.conditions === 1 ? '' : 's'} for participants to confirm`,
        consent.eligibility ? 'Eligibility criteria filled in' : 'No eligibility criteria',
        {
          transcribed: 'Information sheet transcribed from your document — read it through',
          drafted: 'Information sheet drafted as you asked — complete and have it approved before use',
          placeholder: 'Information sheet couldn’t be transcribed — placeholder to replace',
          none: 'No information sheet uploaded — placeholder to replace'
        }[consent.informationSheet] || 'Information sheet placeholder to replace',
        'Placeholder privacy statement to replace'
      ]
    },
    unavailable: { status: 'skipped', text: 'Not set up: your team doesn’t have eConsent enabled' },
    notNeeded: { status: 'skipped', text: 'Not set up: nothing mentioned participants giving consent' }
  }[consent?.status] || { status: 'skipped', text: 'Not set up' }

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      closeOnInteractOutside={false}
      size='lg'
      header={
        <Stack direction='row' alignItems='center' gap={3}>
          <Box as='img' src={scorchHelpful} alt='Scorch' h='40px' />
          <Text>Scorch has built your trial</Text>
        </Stack>
      }
      body={
        <Stack gap={6}>
          {documents?.length > 0 &&
            <Section title='Documents read'>
              {documents.map(d => (
                <Row key={d.name} status={d.read ? 'done' : 'failed'}>
                  {d.name}{' '}
                  {d.read
                    ? <Tag size='sm' colorPalette='gray' label={DOCUMENT_KINDS[d.kind] || DOCUMENT_KINDS.other} />
                    : <Text as='span' color='fg.muted'>— couldn’t be read</Text>}
                </Row>
              ))}
            </Section>}

          <Section title='Trial'>
            <Row status='done' notes={trial.details?.length ? [`Also filled in: ${trial.details.join(', ')}`] : undefined}>
              Created <strong>{trial.name}</strong> with a description
            </Row>
          </Section>

          <Section title='Consent'>
            <Row status={consentRow.status} notes={consentRow.notes}>{consentRow.text}</Row>
          </Section>

          <Section title='Stages'>
            {stages?.length
              ? stages.map((s, i) => (
                <Row key={i} status={s.created ? 'done' : 'failed'} notes={[s.schedule && `Schedule: ${s.schedule}`, ...(s.notes || [])].filter(Boolean)}>{s.name}</Row>
              ))
              : <Row status='skipped'>No stages were planned</Row>}
          </Section>

          {warnings?.length > 0 &&
            <Section title='Left for you'>
              {warnings.map((w, i) => <Row key={i} status='todo'>{w}</Row>)}
            </Section>}
        </Stack>
      }
      footer={<Button colorPalette='primary' onClick={onClose}>Go to trial</Button>}
    />
  )
}
